/**
 * Authentication Hook & Provider
 *
 * Centralises Supabase auth state for the entire application. Exposes
 * the current `User`, `Session`, role list, and active-account flag.
 *
 * Initialisation flow (race-condition-hardened):
 *  1. `initializeAuth` calls `getSession()` once on mount.
 *  2. If a session exists, `fetchUserData` loads roles + profile atomically.
 *  3. `loading` remains `true` until both steps resolve.
 *  4. `onAuthStateChange` handles subsequent events (token refresh, sign-out)
 *     in the background without re-triggering the global loading spinner.
 *
 * Security posture: deny-by-default — `isActive` starts `false` and is
 * only set `true` when the profile confirms `is_active`.
 */

import * as Sentry from "@sentry/react";
import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { installAuthInterceptor } from "@/lib/supabase-auth-interceptor";

export type AppRole = "admin" | "manager" | "user" | "viewer" | "campaign_approver" | "buyer_reviewer" | "issuer_staff";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  roles: AppRole[];
  isActive: boolean;
  hasRole: (role: AppRole) => boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/** Auth Provider component. */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState<AppRole[]>([]);
  const [isActive, setIsActive] = useState(false); // R3: deny-by-default

  const fetchUserData = useCallback(async (userId: string) => {
    // R2: Fetch roles and profile atomically before setting loading=false
    const [rolesResult, profileResult] = await Promise.allSettled([
      supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", userId),
      supabase
        .from("profiles")
        .select("is_active")
        .eq("user_id", userId)
        .single(),
    ]);

    let fetchedRoles: AppRole[] = [];
    let fetchedIsActive = false;

    if (rolesResult.status === "fulfilled" && rolesResult.value.data) {
      fetchedRoles = rolesResult.value.data.map((r) => r.role as AppRole);
      setRoles(fetchedRoles);
    }

    if (profileResult.status === "fulfilled" && profileResult.value.data) {
      fetchedIsActive = profileResult.value.data.is_active ?? false;
      setIsActive(fetchedIsActive);
    }

    // Sentry user identity is set once in initializeAuth where email is available.
    // Only update the ID here to avoid overwriting email with undefined.
    Sentry.setUser({ id: userId });

    // #9 — Searchable tags for Sentry event filtering
    Sentry.setTag("user_role", fetchedRoles[0] ?? "none");
    Sentry.setTag("is_active", String(fetchedIsActive));

    // #14 — Rich structured context for error diagnostics
    Sentry.setContext("session", {
      roles: fetchedRoles,
      isActive: fetchedIsActive,
      userId,
    });
  }, []);

  useEffect(() => {
    let isMounted = true;
    let initialized = false;

    // Ongoing auth events (TOKEN_REFRESHED, SIGNED_OUT, etc.)
    // Does NOT control loading — that's initializeAuth's job.
    // Skips fetchUserData until initializeAuth completes to prevent duplicate fetches.
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (!isMounted) return;

        setSession(session);
        setUser(session?.user ?? null);

        if (!initialized) return; // initializeAuth handles the first fetch

        // Detect expired refresh token: TOKEN_REFRESHED fires but session is null
        if (event === "TOKEN_REFRESHED" && !session) {
          setRoles([]);
          setIsActive(false);
          toast({
            title: "Session expired",
            description: "Please sign in again",
            variant: "destructive",
          });
          return;
        }

        if (session?.user) {
          // Fire-and-forget: update roles/profile in background
          fetchUserData(session.user.id);
          // Store last_sign_in_at from auth metadata
          if (event === "SIGNED_IN") {
            supabase
              .from("profiles")
              .update({ last_sign_in_at: new Date().toISOString() })
              .eq("user_id", session.user.id)
              .then(() => {});
          }
        } else {
          setRoles([]);
          setIsActive(false); // R3: deny-by-default on logout
        }
      }
    );

    // One-time initial load: getSession → fetchUserData → setLoading(false)
    const initializeAuth = async () => {
      try {
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        if (!isMounted) return;

        setSession(currentSession);
        setUser(currentSession?.user ?? null);

        if (currentSession?.user) {
          const fullName = currentSession.user.user_metadata?.full_name;
          Sentry.setUser({
            id: currentSession.user.id,
            email: currentSession.user.email,
            username: fullName || undefined,
          });
          await fetchUserData(currentSession.user.id);
        }
      } finally {
        if (isMounted) {
          initialized = true;
          setLoading(false);
        }
      }
    };

    initializeAuth();

    const cleanupInterceptor = installAuthInterceptor();

    return () => {
      isMounted = false;
      subscription.unsubscribe();
      cleanupInterceptor();
    };
  }, [fetchUserData]);

  const signOut = async () => {
    Sentry.addBreadcrumb({ category: "auth", message: "User signing out", level: "info" });
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setRoles([]);
    setIsActive(false);
    Sentry.setUser(null);
  };

  const hasRole = (role: AppRole) => roles.includes(role);

  return (
    <AuthContext.Provider value={{ user, session, loading, roles, isActive, hasRole, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

/** Manages auth state and operations. */
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

// AppRole is exported directly from the type declaration above
